import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { QueryPartStrategy } from '../../../advanced-search/filters/base/entities/query-part-strategy';
import { QueryPart } from '../../../advanced-search/filters/base/entities/query-part.class';
import { ApiQuery, Combine, Restriction } from '../../../core/api/entities/api-query.entity';
import { EmptyStructuredQuery } from '../../../core/query/entities/empty-structured-query.constant';
import { RecordTypesService } from '../../../core/record-types/services/record-types.service';
import { RecordType, TernaryLogic } from '../../../core/records/entities/metadata/metadata.entity';
import { PartialRecord } from '../../../core/records/entities/partial-record.class';
import { ShRecordService } from '../../../core/records/services/record.service';
import {
    defaultSortInfo,
    titleAscSortInfo
} from '../../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { RecordTreeEntity } from '../entities/record-tree.entity';

@Injectable()
export class ShRecordTreeSandbox {
    private recordsService: ShRecordService = inject(ShRecordService);
    private recordTypeService: RecordTypesService = inject(RecordTypesService);

    private RESULTS_PER_PAGE: number = 100;

    // To expand with background query
    public getRecordTree(
        structureEntity?: RecordTreeEntity,
        offset: number = 0
    ): Observable<PaginatedResults<RecordTreeEntity>> {
        const paginationInfo: PaginationInfo = {
            page: offset / this.RESULTS_PER_PAGE,
            resultsPerPage: this.RESULTS_PER_PAGE
        };

        // TOFIX: Paginated vs PaginatedResults usage => I like Paginated better since flatter structure...
        const childRecordResults$: Observable<PaginatedResults<PartialRecord>> = structureEntity
            ? this.getChildRecords(structureEntity, paginationInfo)
            : this.getRootRecords(paginationInfo);

        return childRecordResults$.pipe(
            map(childRecordResults => ({
                ...childRecordResults,
                results: childRecordResults.results.map(childRecord =>
                    this.partialRecordToRecordTreeEntity(childRecord)
                )
            }))
        );
    }

    private getChildRecords(
        structureEntity: RecordTreeEntity,
        paginationInfo: PaginationInfo
    ): Observable<PaginatedResults<PartialRecord>> {
        const parentIdQueryPart: QueryPart = new QueryPart({
            field: 'ParentRecordId',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: [structureEntity.referenceCode]
        });

        const recordTypes: RecordType[] = this.recordTypeService
            .getAllowedChildren(structureEntity.referenceType)
            .map(apiRecordType => apiRecordType.Name);
        return this.getRecordsOfType(recordTypes, paginationInfo, [parentIdQueryPart]);
    }

    private getRootRecords(paginationInfo: PaginationInfo): Observable<PaginatedResults<PartialRecord>> {
        const recordTypes: RecordType[] = this.recordTypeService
            .getTopLevelTypes()
            .map(apiRecordType => apiRecordType.Name);

        return this.fetchClassifications().pipe(
            switchMap(classificationIds => {
                // Match at least one of the classification ids as parent
                const parentIdQueryPart: QueryPart = new QueryPart({
                    field: 'ParentRecordId',
                    strategy: QueryPartStrategy.MatchAtLeastOne,
                    values: classificationIds
                });
                return this.getRecordsOfType(recordTypes, paginationInfo, [parentIdQueryPart]);
            })
        );
    }

    private getRecordsOfType(
        recordTypes: string[],
        paginationInfo: PaginationInfo,
        extraQueryParts: QueryPart[] = []
    ): Observable<PaginatedResults<PartialRecord>> {
        const includedRecordTypesQueryPart: QueryPart = new QueryPart({
            field: 'RecordType',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: recordTypes
        });

        // TODO: Replace by actual backgroundquery
        // Now manually searching on everything:
        const allZonesSearch = new QueryPart({
            // Counteract the default filter on IsInIngestSpace:false
            field: 'IsInIngestSpace',
            strategy: QueryPartStrategy.MatchAll,
            values: [TernaryLogic.both]
        });

        const queryParts: QueryPart[] = [...extraQueryParts, includedRecordTypesQueryPart, allZonesSearch];

        const fullQuery: ApiQuery = new ApiQuery(queryParts);

        return this.recordsService.getRecords(paginationInfo, titleAscSortInfo, fullQuery);
    }

    // #region Classifications
    // NOTE: Since classifications service is very integrated into the shared library I take a shortcut to retrieve them here

    private fetchClassifications(): Observable<string[]> {
        const paginationInfo: PaginationInfo = {
            page: 0,
            resultsPerPage: 100
        };

        const allZonesSearch = new QueryPart({
            // Counteract the default filter on IsInIngestSpace:false
            field: 'IsInIngestSpace',
            strategy: QueryPartStrategy.MatchAll,
            values: [TernaryLogic.both]
        });

        const onlyClassifications: QueryPart = new QueryPart({
            field: 'RecordType',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: ['Classification']
        });

        const restriction: Restriction = Combine.And(allZonesSearch, onlyClassifications);

        const query: ApiQuery = new ApiQuery(EmptyStructuredQuery, restriction);

        // Shortcut. Just fetching first 100 for now
        return this.recordsService.getRecords(paginationInfo, defaultSortInfo, query).pipe(
            map(result => result.results.map(record => record.getRecordId())),
            take(1),
            shareReplay()
        );
    }

    // #endregion
    // #region Utils
    private partialRecordToRecordTreeEntity(record: PartialRecord): RecordTreeEntity {
        return {
            referenceCode: record.getRecordId(),
            referenceTitle: record.getTitle(),
            referenceType: record.getRecordType(),
            hasChildren: record.hasNonRepresentationChildren()
        };
    }
    // #endregion
}
